// `avatar autopilot <init|run|status|approve|pause|resume|answer|redirect|revert|abort|doctor|schema|capabilities|unlock|archive|resolve-deploy>` (16 lệnh con)
// (spec §3.3): init/run here (status: autopilot-status-command.ts); 7 inbox-only control commands live in autopilot-control-directive-commands.ts.
import { join } from "node:path";
import type { Command } from "commander";
import { registerAutopilotControlDirectiveCommands } from "../lib/autopilot/autopilot-control-directive-commands.js";
import { parsePositiveTickCount } from "../lib/autopilot/cli-positive-integer-option.js";
import { readUnusedConfigKeyWarnings } from "../lib/autopilot/config-keys-not-read-in-v1.js";
import {
  formatInitLeftoverHints,
  listMissionIdsForHint,
} from "../lib/autopilot/mission-archive.js";
import { initMission } from "../lib/autopilot/mission-initializer.js";
import { InitValidationError } from "../lib/autopilot/mission-initializer-errors.js";
import { addStdinValues } from "../lib/autopilot/mission-known-secret-values-registry.js";
import { resolveMission } from "../lib/autopilot/mission-resolver.js";
import { preflightMissionTestEnvForRun } from "../lib/autopilot/mission-secrets-test-env-run-preflight.js";
import {
  type ReleaseSecretsStdin,
  readReleaseSecretsFromStdin,
} from "../lib/autopilot/release-secrets-stdin-reader.js";
import { runAutopilotRunCommand } from "../lib/autopilot/run-command-cli-wiring.js";
import { runCommandExitCode } from "../lib/autopilot/run-command-outcome-observability.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { getActiveFlag, requireFlag } from "../lib/non-interactive-context.js";
import { log } from "../lib/terminal-logger.js";
import { AVATAR_HOME } from "../lib/user-config-store.js";
import { registerAutopilotArchiveCommand } from "./autopilot-archive-command.js";
import { registerAutopilotDoctorCommand } from "./autopilot-doctor-command.js";
import { registerAutopilotResolveDeployCommand } from "./autopilot-resolve-deploy-command.js";
import { registerAutopilotStatelessSubcommands } from "./autopilot-stateless-subcommands-registration.js";
import { registerAutopilotStatusCommand } from "./autopilot-status-command.js";
import { registerAutopilotUnlockCommand } from "./autopilot-unlock-command.js";

const DEFAULT_GLOBAL_CONFIG_PATH = join(AVATAR_HOME, "autopilot-config.json");
const DEFAULT_MACHINE_LOCK_PATH = join(AVATAR_HOME, "autopilot.lock");

async function resolveWorkspaceRoot(repoFlag: string | undefined): Promise<string> {
  const { resolveAvatarWorkspaceRootFromCwd } = await import(
    "../lib/resolve-avatar-workspace-root-from-cwd.js"
  );
  const root = resolveAvatarWorkspaceRootFromCwd(repoFlag ?? process.cwd());
  if (!root)
    throw new Error("Không tìm thấy Avatar workspace (thiếu .claude/avatar.json) từ cwd/--repo.");
  return root;
}

export function registerAutopilotCommand(program: Command): void {
  const autopilot = program
    .command("autopilot")
    .description("Chạy mission tự hành (Autopilot V2.1)");

  autopilot
    .command("init")
    .description("Khởi tạo mission từ request file (startbuild entry point)")
    .option("--request-file <path>", "Đường dẫn init request JSON")
    .action(async (opts: { requestFile?: string }) => {
      const requestFilePath = requireFlag(
        opts.requestFile ?? getActiveFlag<string>("requestFile"),
        {
          flag: "--request-file",
          command: "autopilot init",
          hint: "--request-file <path>",
        },
      );
      const workspaceRoot = await resolveWorkspaceRoot(undefined);
      const packSourceDir = join(workspaceRoot, ".claude", "pack");
      // Req 7: snapshot BEFORE so a failure below can name exactly which
      // mission dir `initMission` left behind (never log.*/stdout — that
      // channel is reserved for the JSON receipt of a SUCCESSFUL init).
      const before = await listMissionIdsForHint(workspaceRoot);
      try {
        const receipt = await initMission({
          requestFilePath,
          workspaceRoot,
          packSourceDir,
          actorOsAccount: process.env.USER ?? process.env.LOGNAME ?? "unknown",
          globalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
        });
        // Req 3 (D7, C5): stderr, never the logger — --non-interactive stdout
        // stays exactly 1 JSON line (machine-readable-json-output-contract.ts).
        for (const w of await readUnusedConfigKeyWarnings(DEFAULT_GLOBAL_CONFIG_PATH)) {
          process.stderr.write(`⚠ ${w}\n`);
        }
        writeMachineReadableJson({ command: "autopilot init", receipt });
      } catch (err) {
        const after = await listMissionIdsForHint(workspaceRoot);
        for (const hint of formatInitLeftoverHints(before, after))
          process.stderr.write(`${hint}\n`);
        // Operator input error (config/base_sha/BLUEPRINT…), not a crash.
        if (err instanceof InitValidationError) {
          process.stderr.write(`✗ ${err.message}\n`);
          process.exit(1);
        }
        throw err;
      }
    });

  autopilot
    .command("run")
    .description("Chạy supervisor foreground cho một mission (dispatch tới idle)")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--once", "Chạy đúng 1 tick rồi dừng (tương đương --max-ticks 1)")
    .option("--max-ticks <n>", "Giới hạn số tick control loop (test/E2E)", parsePositiveTickCount)
    .option(
      "--release-secrets-stdin",
      "Đọc secret release (KEY=VALUE) từ stdin pipe, chỉ giữ trong bộ nhớ — cho deploy/verify sau G3",
    )
    .action(
      async (opts: {
        mission?: string;
        repo?: string;
        once?: boolean;
        maxTicks?: number;
        releaseSecretsStdin?: boolean;
      }) => {
        const workspaceRoot = await resolveWorkspaceRoot(opts.repo);
        const resolved = await resolveMission({ workspaceRoot, explicitMissionId: opts.mission });
        const maxTicks = opts.once ? 1 : opts.maxTicks;
        // Phase 04a (§4.2 (5)): `test.env` sai quyền/owner ⇒ chặn TRƯỚC dispatch,
        // trước cả machine lock — thông điệp chỉ đường dẫn, không giá trị.
        const secrets = await preflightMissionTestEnvForRun(resolved.missionId);
        if (!secrets.ok) {
          process.stderr.write(`✗ ${secrets.message}\n`);
          process.exit(1);
        }
        // Phase 04b (§4.2 (11)/(12)): chỉ đọc stdin khi có cờ (không cờ ⇒ pipe
        // không bị tiêu thụ); TTY/sai dạng ⇒ chặn trước dispatch. Map chỉ ở RAM.
        let releaseSecretsStdin: ReleaseSecretsStdin | undefined;
        if (opts.releaseSecretsStdin === true) {
          const read = await readReleaseSecretsFromStdin();
          if (!read.ok) {
            process.stderr.write(`✗ ${read.message}\n`);
            process.exit(1);
          }
          releaseSecretsStdin = read.values;
          addStdinValues(resolved.missionId, { ...read.values });
        }

        const result = await runAutopilotRunCommand({
          workspaceRoot,
          missionId: resolved.missionId,
          missionHome: resolved.missionHome,
          controlRoot: resolved.controlRoot,
          repoFlag: opts.repo,
          machineLockPath: DEFAULT_MACHINE_LOCK_PATH,
          globalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
          maxTicks,
          log,
          releaseSecretsStdin,
        });
        // H14 D15: 0 only when the ticket loop is done AND the release flow settled.
        if (runCommandExitCode(result) !== 0) process.exit(runCommandExitCode(result));
      },
    );

  registerAutopilotStatusCommand(autopilot, { resolveWorkspaceRoot });

  const lockDeps = { resolveWorkspaceRoot, machineLockPath: DEFAULT_MACHINE_LOCK_PATH };
  registerAutopilotControlDirectiveCommands(autopilot, lockDeps);

  registerAutopilotDoctorCommand(autopilot, {
    resolveWorkspaceRoot,
    defaultGlobalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
    machineLockPath: DEFAULT_MACHINE_LOCK_PATH,
  });

  // F2 `schema <name>` + V2 §7 `capabilities --json` (no mission state).
  registerAutopilotStatelessSubcommands(autopilot);

  registerAutopilotUnlockCommand(autopilot, lockDeps);
  registerAutopilotArchiveCommand(autopilot, lockDeps);
  // A5: exit from blocked:deploy_outcome_unknown other than archive.
  registerAutopilotResolveDeployCommand(autopilot, {
    ...lockDeps,
    globalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
  });
}
